import { lazy } from "react";
import type { RouteObject } from "react-router-dom";
import { Navigate } from "react-router-dom";
const NotFound = lazy(() => import("../pages/NotFound"));
const Home = lazy(() => import("../pages/home/page"));
const Apply = lazy(() => import("../pages/apply/page"));
const GalleryPage = lazy(() => import("../pages/gallery/page"));
const StandardStretchTents = lazy(() => import("../pages/products/standard-stretch-tents/page"));
const CustomStretchTents = lazy(() => import("../pages/products/custom-stretch-tents/page"));
const CustomPrintedSkins = lazy(() => import("../pages/products/custom-printed-skins/page"));
const BespokeDecor = lazy(() => import("../pages/products/bespoke-decor/page"));
const ProductsPage = lazy(() => import("../pages/products/page"));
const BlogPage = lazy(() => import("../pages/blog/page"));
const ContactPage = lazy(() => import("../pages/contact/page"));
const FabricAndColoursPage = lazy(() => import("../pages/products/fabric-and-colours/page"));
// Rigging Equipment page intentionally unrouted (hidden, not deleted) - code
// still lives at ../pages/products/rigging-equipment/page, undecided if it
// comes back or gets removed for good.
const EngineeringSafetyPage = lazy(() => import("../pages/engineering-safety/page"));
const AboutPage = lazy(() => import("../pages/about/page"));
const StretchTentSizesPage = lazy(() => import("../pages/blog/stretch-tent-sizes/page"));
const StretchTentRentalGuidePage = lazy(() => import("../pages/blog/stretch-tent-rental-guide/page"));
const LightningInABottlePage = lazy(() => import("../pages/blog/lightning-in-a-bottle/page"));
const EnhanceFestivalAppealPage = lazy(() => import("../pages/blog/enhance-festival-appeal/page"));
const LuxuryBedouinTentsPage = lazy(() => import("../pages/blog/luxury-bedouin-tents/page"));
const BehindScenePopProTentsPage = lazy(() => import("../pages/blog/behind-scenes-pop-pro-tents/page"));
const FestivalStretchTentPage = lazy(() => import("../pages/blog/festival-stretch-tent/page"));
const TenReasonsStretchTentPage = lazy(() => import("../pages/blog/10-reasons-stretch-tent/page"));
const StretchTentFabricsPage = lazy(() => import("../pages/blog/stretch-tent-fabrics/page"));

const routes: RouteObject[] = [
  {
    path: "/",
    element: <Home />,
  },
  {
    path: "/apply",
    element: <Apply />,
  },
  {
    path: "/gallery",
    element: <GalleryPage />,
  },
  {
    path: "/products",
    element: <ProductsPage />,
  },
  {
    path: "/products/standard-stretch-tents",
    element: <StandardStretchTents />,
  },
  {
    path: "/products/custom-stretch-tents",
    element: <CustomStretchTents />,
  },
  {
    path: "/products/custom-printed-skins",
    element: <CustomPrintedSkins />,
  },
  {
    path: "/products/bespoke-decor",
    element: <BespokeDecor />,
  },
  {
    path: "/products/fabric-and-colours",
    element: <FabricAndColoursPage />,
  },
  {
    path: "/engineering-safety",
    element: <EngineeringSafetyPage />,
  },
  {
    path: "/blog",
    element: <BlogPage />,
  },
  {
    path: "/blog/suitable-stretch-tent-sizes-for-your-outdoor-event",
    element: <StretchTentSizesPage />,
  },
  {
    path: "/blog/stretch-tent-rentals-guide-tips-considerations",
    element: <StretchTentRentalGuidePage />,
  },
  {
    path: "/blog/lightning-in-a-bottle-festival",
    element: <LightningInABottlePage />,
  },
  {
    path: "/blog/enhance-festival-appeal-stretch-tents",
    element: <EnhanceFestivalAppealPage />,
  },
  {
    path: "/blog/luxury-bedouin-tents-exclusive-events",
    element: <LuxuryBedouinTentsPage />,
  },
  {
    path: "/blog/behind-the-scenes-pop-pro-tents",
    element: <BehindScenePopProTentsPage />,
  },
  {
    path: "/blog/festival-stretch-tent-outdoor-music-festival",
    element: <FestivalStretchTentPage />,
  },
  {
    path: "/blog/10-reasons-choose-stretch-tent-next-event",
    element: <TenReasonsStretchTentPage />,
  },
  {
    path: "/blog/our-stretch-tent-fabrics-outperform-other-fabrics",
    element: <StretchTentFabricsPage />,
  },
  {
    path: "/contact",
    element: <ContactPage />,
  },
  {
    path: "/about",
    element: <AboutPage />,
  },

  // ── REDIRECTS: Renamed product pages ──
  { path: "/products/printed-stretch-tents", element: <Navigate to="/products/custom-printed-skins" replace /> },

  // ── REDIRECTS: Short slugs → existing pages ──
  { path: "/bedouin-tent", element: <Navigate to="/blog/luxury-bedouin-tents-exclusive-events" replace /> },
  { path: "/festival-tent", element: <Navigate to="/blog/festival-stretch-tent-outdoor-music-festival" replace /> },
  { path: "/wedding-tent", element: <Navigate to="/contact" replace /> },
  { path: "/stretch-tents", element: <Navigate to="/products/standard-stretch-tents" replace /> },
  { path: "/event-tent", element: <Navigate to="/products/standard-stretch-tents" replace /> },
  { path: "/wedding-tent-rental", element: <Navigate to="/contact" replace /> },
  { path: "/join-the-team", element: <Navigate to="/apply" replace /> },
  { path: "/event-tent-rentals", element: <Navigate to="/products/standard-stretch-tents" replace /> },
  { path: "/festival-tent-rental", element: <Navigate to="/contact" replace /> },
  { path: "/get-a-quote", element: <Navigate to="/contact" replace /> },
  { path: "/how-stretch-tents-work", element: <Navigate to="/products/standard-stretch-tents" replace /> },
  { path: "/stretch-tents-for-sale", element: <Navigate to="/products/custom-stretch-tents" replace /> },
  { path: "/privacy-policy", element: <Navigate to="/" replace /> },
  { path: "/terms-and-conditions", element: <Navigate to="/" replace /> },

  // ── REDIRECTS: Old category/tent URL patterns ──
  { path: "/category/stretch-tents", element: <Navigate to="/products/standard-stretch-tents" replace /> },
  { path: "/category/stretch-tents/page/:page", element: <Navigate to="/products/standard-stretch-tents" replace /> },
  { path: "/tent/:slug", element: <Navigate to="/products/standard-stretch-tents" replace /> },

  // ── REDIRECTS: Blog pagination ──
  { path: "/blog/page/:page", element: <Navigate to="/blog" replace /> },

  // ── REDIRECTS: Blog posts under old slugs (no /blog/ prefix) ──
  { path: "/stretch-tent-rentals-guide-tips-considerations", element: <Navigate to="/blog/stretch-tent-rentals-guide-tips-considerations" replace /> },
  { path: "/festival-stretch-tent-outdoor-music-festival", element: <Navigate to="/blog/festival-stretch-tent-outdoor-music-festival" replace /> },
  { path: "/enhance-your-festivals-appeal-with-stretch-tents", element: <Navigate to="/blog/enhance-festival-appeal-stretch-tents" replace /> },
  { path: "/luxury-bedouin-tent-exclusive-events-benefits-features", element: <Navigate to="/blog/luxury-bedouin-tents-exclusive-events" replace /> },
  { path: "/pop-pro-tents-leaders-in-stretch-tent-rentals", element: <Navigate to="/blog/behind-the-scenes-pop-pro-tents" replace /> },
  { path: "/suitable-stretch-tent-sizes-for-your-outdoor-event", element: <Navigate to="/blog/suitable-stretch-tent-sizes-for-your-outdoor-event" replace /> },

  // ── REDIRECTS: Old blog slugs that don't have exact matches → blog index ──
  { path: "/stretch-tents-for-music-festivals-and-concerts", element: <Navigate to="/blog" replace /> },
  { path: "/compare-stretch-tents-bedouin-tents", element: <Navigate to="/blog/luxury-bedouin-tents-exclusive-events" replace /> },
  { path: "/planning-a-wellness-event-rent-a-stretch-tent", element: <Navigate to="/blog" replace /> },
  { path: "/heres-why-stretch-tents-suit-all-types-of-events", element: <Navigate to="/blog" replace /> },
  { path: "/stretch-tent-vs-marquee-which-is-right-for-you", element: <Navigate to="/blog" replace /> },

  {
    path: "*",
    element: <NotFound />,
  },
];

export default routes;